import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp } from "lucide-react";
import type { JSX } from "react";

import type { DiagramBlock, DiagramNode } from "@/lib/reports";

import { ReportVisualBoundary } from "./ReportVisualBoundary";

interface ReportDiagramProps {
  block: DiagramBlock;
}

function orderedNodes(block: DiagramBlock): DiagramNode[] {
  return block.direction === "BT" || block.direction === "RL"
    ? [...block.nodes].reverse()
    : block.nodes;
}

function Connector({ direction }: { direction: DiagramBlock["direction"] }): JSX.Element {
  const Icon =
    direction === "TB"
      ? ArrowDown
      : direction === "BT"
        ? ArrowUp
        : direction === "RL"
          ? ArrowLeft
          : ArrowRight;
  return <Icon aria-hidden="true" className="shrink-0 text-[var(--report-accent)]" size={18} />;
}

export function ReportDiagram({ block }: ReportDiagramProps): JSX.Element {
  const nodes = orderedNodes(block);
  const horizontal = block.direction === "LR" || block.direction === "RL";
  const labels = new Map(block.nodes.map((node) => [node.node_id, node.label]));
  return (
    <div className="space-y-4">
      <ReportVisualBoundary
        fallback={
          <div className="rounded-lg border border-dashed border-[var(--report-border)] bg-[var(--report-muted)] p-4 text-sm text-[var(--report-muted-fg)]">
            Diagrama no disponible; se muestra su representación textual.
          </div>
        }
      >
        <div
          aria-hidden="true"
          className={`flex gap-2 overflow-x-auto rounded-xl bg-[var(--report-muted)] p-4 ${
            horizontal ? "items-center" : "flex-col items-stretch"
          }`}
          data-diagram-direction={block.direction}
          data-testid={`report-diagram-${block.diagram_kind}`}
        >
          {nodes.map((node, index) => (
            <div className={`contents ${horizontal ? "" : "text-center"}`} key={node.node_id}>
              {index > 0 ? <Connector direction={block.direction} /> : null}
              <div className="min-w-32 rounded-lg border border-[var(--report-border)] bg-[var(--report-bg)] px-3 py-3 text-center text-sm font-medium shadow-sm">
                {node.label}
              </div>
            </div>
          ))}
        </div>
      </ReportVisualBoundary>

      <p className="leading-7">{block.explanation.text}</p>

      <details className="rounded-lg border border-[var(--report-border)]">
        <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
          Ver estructura del diagrama
        </summary>
        <div className="grid gap-4 px-3 pb-3 text-sm sm:grid-cols-2">
          <div>
            <h3 className="mb-2 font-semibold">Nodos</h3>
            <ol className="list-decimal space-y-1 pl-5" aria-label={`Nodos de ${block.heading}`}>
              {block.nodes.map((node) => (
                <li key={node.node_id}>{node.label}</li>
              ))}
            </ol>
          </div>
          <div>
            <h3 className="mb-2 font-semibold">Conexiones</h3>
            <ol
              className="list-decimal space-y-1 pl-5"
              aria-label={`Conexiones de ${block.heading}`}
            >
              {block.edges.map((edge) => (
                <li key={edge.edge_id}>
                  {labels.get(edge.source_node_id) ?? edge.source_node_id} →{" "}
                  {labels.get(edge.target_node_id) ?? edge.target_node_id}
                  {edge.label ? ` · ${edge.label}` : ""}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </details>
    </div>
  );
}
